import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation23() {
  const { lesson, course, accent } = findLesson("piano-foundation", "23");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF23-Pendulum-Animated.mp4"
        caption={`A swinging arc that widens — 1, 2, 3, 4 notes out — the thumb-cross-under reach`}
        hint={`≈ 22 sec · the thumb is the pivot`}
      />
      <KeyHighlights>
        <Highlight>The piano is one continuous row of keys, unlike a guitar where you can jump strings. The thumb is what lets your hand <strong>cross over</strong> to keep travelling.</Highlight>
        <Highlight>The exercise is in <strong>E major</strong>: <strong>1 up 1 down</strong>, then <strong>2 up 2 down</strong>, then <strong>3 up 3 down</strong>, then <strong>4 up 4 down</strong>. Each stage extends the pendulum further.</Highlight>
        <Highlight>Going <em>up</em>, the <strong>thumb tucks under</strong> the index. Coming <em>down</em>, the <strong>middle (or index) crosses over</strong> the thumb.</Highlight>
        <Highlight>Avoid crossing the ring or pinky over the thumb — too awkward. Use the middle finger as the main crosser, with the index as an emergency backup.</Highlight>
        <Highlight>Once the symmetric pendulums are solid, break the symmetry — try <strong>2 up 1 down</strong>, <strong>1 up 2 down</strong> — and the pendulum becomes a melody-generator.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Why this exists" title="The piano can't switch strings" accent={accent.hex}>
        <Pull>
          Whenever a guitar runs out of strings, what do they do? They just go to the
          next string which is right there. But on a piano we just have one of them
          which is just this one linear thing with white and black notes. So one thing
          which we have under our disposal is our thumb.
        </Pull>

        <p>
          The Pendulum exercise solves the most fundamental travel problem on the keyboard.
          You only have five fingers per hand, but a phrase might want to walk up six, seven,
          eight notes. The solution is to slip the thumb under (or hop a finger over) so the
          hand resets without losing the line.
        </p>

        <Vocab>
          <Term name="Thumb-under" def="when ascending, slipping the thumb under the index/middle finger so the hand can keep climbing." />
          <Term name="Cross-over" def="when descending, crossing the middle (or index) finger over the thumb to keep descending." />
          <Term name="Pendulum" def="the name for the swing-up-and-down motion that drills both crossings together." />
        </Vocab>
      </Insight>

      <Insight eyebrow="The setup" title="E major scale, hand parked on E" accent={accent.hex}>
        <p>
          Use <strong>E major</strong>: E, F♯, G♯, A, B, C♯, D♯. Park your right hand with
          the thumb on E. Then start the pendulum stages:
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "E4", color: "#ef4444", label: "1" },
            { pitch: "F#4", color: "#f97316", label: "2" },
            { pitch: "G#4", color: "#eab308", label: "3" },
            { pitch: "A4", color: "#22c55e", label: "4" },
            { pitch: "B4", color: "#06b6d4", label: "5" },
            { pitch: "C#5", color: "#3b82f6", label: "6" },
            { pitch: "D#5", color: "#a855f7", label: "7" },
            { pitch: "E5", color: "#ec4899", label: "8" },
          ]}
          height={180}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="23"
          id="emajor-scale"
          mode="sequence"
          title="E major scale · the Pendulum"
          prompt="Climb the E major scale with the thumb tucking under: E F♯ G♯ A B C♯ D♯ E."
          notes={["E4", "F#4", "G#4", "A4", "B4", "C#5", "D#5", "E5"]}
          startOctave={4}
          octaves={2}
        />

        <p className="prose-nsm text-sm">
          Each stage of the pendulum reaches one note further from the start, then comes back.
          The exercise is symmetric — the swing always returns to the starting E.
        </p>
      </Insight>

      <Insight eyebrow="The four stages" title="1 up 1 down · 2 up 2 down · 3 up 3 down · 4 up 4 down" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>1 up · 1 down.</strong> E - F♯ - E. The thumb plays E both times; index
            plays F♯. Tiny swing. No crossing needed yet — but the wrist motion is the same
            small lift you'll use later.
          </Step>
          <Step>
            <strong>2 up · 2 down.</strong> E - F♯ - G♯ - F♯ - E. Now you have a choice on
            the way down: cross the middle finger over the thumb, OR keep the index and
            bring the thumb back. Practise both — they're both useful in different songs.
          </Step>
          <Step>
            <strong>3 up · 3 down.</strong> E - F♯ - G♯ - A - G♯ - F♯ - E. Going up is easy.
            Coming down, cross the middle finger over the thumb (don't cross the ring — it's
            annoying mechanically). This stage creates a <em>triplet vibe</em>.
          </Step>
          <Step>
            <strong>4 up · 4 down.</strong> E - F♯ - G♯ - A - B - A - G♯ - F♯ - E. The
            pinky reaches B. Coming back, cross the middle over the thumb again. The
            pendulum is now at maximum swing — five fingers don't reach further than this
            without retraining.
          </Step>
        </Steps>

        <Tip>
          Keep the elbow still. The hand glides; the thumb does the work. Don't move the
          elbow to chase the thumb — that's unnecessary motion that slows everything down.
          <em>"This is absolutely unnecessary. So don't do that. Just keep a minor
          movement. The thumb will do the rest."</em>
        </Tip>
      </Insight>

      <Insight eyebrow="The crossing rule" title="Middle is the main crosser · index is the backup" accent={accent.hex}>
        <Pull>
          Don't bother crossing the ring and the pinky. It's going to be very difficult
          mechanically for your hand.
        </Pull>

        <p>
          The middle finger has the right length and pivot angle to clear the thumb cleanly.
          The index can do it in a pinch but it's tighter. The ring and pinky simply don't
          rotate that way — forcing them to cross will hurt the hand and stall the line.
        </p>

        <Tip>
          Take small breaks. The thumb has its own muscle near the base of the palm; it can
          tense up faster than the other muscles. If it starts to ache, pause for a few
          seconds before resuming. The other fingers can run for ages — the thumb cannot.
        </Tip>
      </Insight>

      <Insight eyebrow="Sing the swaras" title="Train the ear at the same time" accent={accent.hex}>
        <p>
          Singing while you play locks the line into the ear, not just the fingers. Calling
          out numbers (1-2-3-4-5) gets messy fast — and so do letter names. The
          <strong> swaras</strong> (Sa-Re-Ga-Ma-Pa-Dha-Ni) sound smoother on the tongue and
          carry over to any scale.
        </p>

        <Pull>
          With India we learn a lot about rhythm and intervals and melody, right? But in
          the West we learn about chords. So you should figure out where each thing
          comes from and just learn in the best way possible.
        </Pull>
      </Insight>

      <Insight eyebrow="Break the symmetry" title="Make a melody from asymmetric pendulums" accent={accent.hex}>
        <p>
          Once 1-up-1-down through 4-up-4-down feels solid, stop being symmetric. Try{" "}
          <strong>2 up · 1 down</strong> — your hand walks slightly forward each cycle. Or
          <strong> 1 up · 2 down</strong> — your hand drifts back. Combine them and a melody
          starts to appear.
        </p>

        <Pull>
          Why do we have to do like an exact pendulum which follows the laws of physics?
          We can break the laws by going maybe two up and one down or maybe one up two
          down.
        </Pull>

        <p>
          Try the same exercise on other scales — A major, F major, G major. The pendulum is
          one of the best ways to know a scale at the back of your head and get your fingers
          to react spontaneously to <em>any</em> melody you're trying to play.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-24"
        label="Lesson 24 · How to GROW as a Pianist"
        why="With Pendulum, Thirds, Zone, and Expansion-Compression all in your routine, the next lesson zooms out — a framework for actually growing as a pianist over months and years."
      />
    </LessonShell>
  );
}
